/*
  Personalization text box.

  Runs in one of two modes (set in the Liquid snippet):
    option   — an existing variant option controls it; surcharge is in the
               variant price, so no add-on is contributed to the cart.
    checkbox — the option has been removed; a checkbox switches it on and the
               hidden "Personalization" add-on product is added to the cart.

  Validation is shared by both. Foil stamping uses a fixed set of stamps, so the
  same character can only repeat as many times as we own stamps for it; laser
  engraving has no such limit and skips the character rules entirely.

  Add-on items are contributed through assets/cart-addons.js rather than by
  intercepting the form here — that keeps gift wrapping and personalization from
  racing each other on submit.
*/
if (!customElements.get('personalization-box')) {
  customElements.define(
    'personalization-box',
    class PersonalizationBox extends HTMLElement {
      connectedCallback() {
        this.safe(() => {
          this.mode = this.dataset.mode;
          this.sectionId = this.dataset.section;
          this.optionPosition = parseInt(this.dataset.optionPosition || '0', 10);
          this.standardMaxLength = parseInt(this.dataset.personalizationMax || '6', 10);
          this.addonVariantId = this.dataset.addonVariantId;
          this.productTitle = this.dataset.productTitle || '';

          this.checkbox = this.querySelector('.personalization-box__checkbox');
          this.fields = this.querySelector('.personalization-box__fields');
          this.input = this.querySelector('.personalization-box__input');
          this.errorEl = this.querySelector('.personalization-box__error');
          this.hintStandardEls = this.querySelectorAll('.personalization-box__hint--standard');
          this.hintLaserEl = this.querySelector('.personalization-box__hint--laser');

          this.engraving = 'standard';

          this.symbolSet1 = ",.;~-'\"";
          this.symbolSet2 = '!@#$%&*+=/?^\u2606\u2605\u2661\u2665';
          this.limits = { upper: 2, lower: 3, digit: 3, symbol1: 3, symbol2: 1 };

          if (this.input) {
            this.input.addEventListener('input', () => this.safe(() => this.validate()));
          }

          if (this.mode === 'checkbox' && this.checkbox) {
            this.prepareCheckboxMode();
            const syncBaseVariant = () => this.safe(() => this.prepareCheckboxMode(true));
            if (document.readyState === 'complete') {
              window.setTimeout(syncBaseVariant, 0);
            } else {
              window.addEventListener('load', syncBaseVariant, { once: true });
            }
            this.checkbox.addEventListener('change', () => this.safe(() => this.toggle(this.checkbox.checked)));
            this.toggle(this.checkbox.checked);
            return;
          }

          // Option mode: follow the variant option.
          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
              if (!event || !event.data || event.data.sectionId !== this.sectionId) return;
              this.safe(() => this.updateForVariant(event.data.variant));
            });
          }
          this.refreshFromDom();
        });
      }

      disconnectedCallback() {
        if (this.unsubscribe) this.unsubscribe();
      }

      // Errors must never leave the field hidden or the button stuck disabled.
      safe(fn) {
        try {
          fn();
        } catch (e) {
          console.error('[personalization-box]', e);
        }
      }

      prepareCheckboxMode(notify = false) {
        const picker = document.querySelector(
          'variant-radios[data-section="' + this.sectionId + '"], variant-selects[data-section="' + this.sectionId + '"]'
        );
        if (!picker || this.optionPosition < 1) return;

        const fieldsets = picker.querySelectorAll('fieldset');
        if (fieldsets.length) {
          const fieldset = fieldsets[this.optionPosition - 1];
          if (!fieldset) return;
          const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
          const off = radios.find((radio) => /^no\b/i.test(String(radio.value).trim()));
          if (!off) return;

          fieldset.classList.add('personalization-option--hidden');
          fieldset.setAttribute('aria-hidden', 'true');
          if (!off.checked || notify) {
            off.checked = true;
            off.dispatchEvent(new Event('change', { bubbles: true }));
          }
          return;
        }

        const selects = picker.querySelectorAll('select');
        const select = selects[this.optionPosition - 1];
        if (!select) return;
        const off = Array.from(select.options).find((option) => /^no\b/i.test(String(option.value).trim()));
        if (!off) return;

        const wrapper = select.closest('.product-form__input') || select.parentElement;
        if (wrapper) {
          wrapper.classList.add('personalization-option--hidden');
          wrapper.setAttribute('aria-hidden', 'true');
        }
        if (select.value !== off.value || notify) {
          select.value = off.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      /* ---------- option mode ---------- */

      readOption() {
        const picker = document.querySelector(
          'variant-radios[data-section="' + this.sectionId + '"], variant-selects[data-section="' + this.sectionId + '"]'
        );
        if (!picker || this.optionPosition < 1) return null;

        const fieldsets = picker.querySelectorAll('fieldset');
        if (fieldsets.length) {
          const fs = fieldsets[this.optionPosition - 1];
          if (!fs) return null;
          const radios = Array.from(fs.querySelectorAll('input[type="radio"]'));
          const checked = radios.find((r) => r.checked);
          return { selected: checked ? checked.value : null, all: radios.map((r) => r.value) };
        }

        const selects = picker.querySelectorAll('select');
        const sel = selects[this.optionPosition - 1];
        if (!sel) return null;
        return { selected: sel.value, all: Array.from(sel.options).map((o) => o.value) };
      }

      hasExplicitSignal(value) {
        const v = String(value).trim().toLowerCase();
        return /^no\b/.test(v) || /^yes\b/.test(v) || /personali[sz]/.test(v);
      }

      isOn(selected, all) {
        const v = String(selected).trim().toLowerCase();
        if (/^no\b/.test(v)) return false;
        if (/^yes\b/.test(v)) return true;
        if (/personali[sz]/.test(v)) return true;
        // No on/off wording on this value. If nothing on the option carries any,
        // the whole option is about personalization (e.g. a foil colour picker
        // with no "None"), so treat every value as on.
        return !(all || []).some((x) => this.hasExplicitSignal(x));
      }

      refreshFromDom() {
        const data = this.readOption();
        if (!data || data.selected === null) return;
        this.setEngraving(data.selected);
        this.toggle(this.isOn(data.selected, data.all));
      }

      updateForVariant(variant) {
        if (!variant) return;
        const values = variant.options || [variant.option1, variant.option2, variant.option3];
        const selected = values[this.optionPosition - 1];
        if (!selected) return;
        const data = this.readOption();
        this.setEngraving(selected);
        this.toggle(this.isOn(selected, data ? data.all : []));
      }

      /* ---------- shared ---------- */

      setEngraving(value) {
        const laser = /laser/i.test(String(value));
        this.engraving = laser ? 'laser' : 'standard';

        this.hintStandardEls.forEach((el) => {
          el.hidden = laser;
        });
        if (this.hintLaserEl) this.hintLaserEl.hidden = !laser;

        if (!this.input) return;
        if (laser) {
          this.input.removeAttribute('maxlength');
        } else {
          this.input.setAttribute('maxlength', String(this.standardMaxLength));
        }
      }

      toggle(show) {
        this.active = !!show;
        if (this.fields) this.fields.hidden = !show;
        if (!this.input) return;

        this.input.disabled = !show;
        if (!show) {
          this.input.value = '';
          this.clearError();
          this.setSubmitBlocked(false);
        } else {
          this.validate();
        }
      }

      validate() {
        if (!this.input || !this.active) return;
        const value = this.input.value;
        this.clearError();

        if (value.length === 0) {
          this.setSubmitBlocked(true);
          return;
        }

        if (this.engraving === 'laser') {
          this.setSubmitBlocked(false);
          return;
        }

        if (value.length > this.standardMaxLength) {
          this.showError('Max ' + this.standardMaxLength + ' characters');
          this.setSubmitBlocked(true);
          return;
        }

        const counts = {};
        for (const ch of value) counts[ch] = (counts[ch] || 0) + 1;

        for (const ch of Object.keys(counts)) {
          const limit = this.limitFor(ch);
          if (limit === null) {
            this.showError('"' + ch + '" is not an available character');
            this.setSubmitBlocked(true);
            return;
          }
          if (counts[ch] > limit) {
            this.showError('Only ' + limit + ' of "' + ch + '" available per order');
            this.setSubmitBlocked(true);
            return;
          }
        }

        this.setSubmitBlocked(false);
      }

      limitFor(ch) {
        if (/[A-Z]/.test(ch)) return this.limits.upper;
        if (/[a-z]/.test(ch)) return this.limits.lower;
        if (/[0-9]/.test(ch)) return this.limits.digit;
        if (this.symbolSet1.indexOf(ch) !== -1) return this.limits.symbol1;
        if (this.symbolSet2.indexOf(ch) !== -1) return this.limits.symbol2;
        return null;
      }

      showError(message) {
        if (!this.errorEl) return;
        this.errorEl.textContent = message;
        this.errorEl.hidden = false;
      }

      clearError() {
        if (!this.errorEl) return;
        this.errorEl.textContent = '';
        this.errorEl.hidden = true;
      }

      setSubmitBlocked(blocked) {
        const button = document.getElementById('ProductSubmitButton-' + this.sectionId);
        if (!button) return;
        if (blocked) {
          button.setAttribute('aria-disabled', 'true');
          button.dataset.personalizationBlocked = 'true';
        } else if (button.dataset.personalizationBlocked === 'true') {
          button.removeAttribute('aria-disabled');
          delete button.dataset.personalizationBlocked;
        }
      }

      /* ---------- add-on contribution ---------- */

      // Only checkbox mode charges separately — in option mode the surcharge is
      // already baked into the selected variant's price.
      getAddonItem(form) {
        if (this.mode !== 'checkbox') return null;
        if (!this.active || !this.addonVariantId) return null;
        if (!this.input || !this.input.value.trim()) return null;

        return {
          id: Number(this.addonVariantId),
          quantity: window.CartAddons.quantity(form),
          properties: { For: window.CartAddons.selectionLabel(this.sectionId, this.productTitle) }
        };
      }
    }
  );
}
